import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import { ErrorState, Skeleton } from '@/components/States';
import { ensureSession } from '@/features/auth/api';
import { getServerOnboardingRoute } from '@/features/onboarding/steps';
import { orgQuery } from '@/features/org/api';
import { AppShell } from '@/features/shell/AppShell';
import { activeOrg, canSwitchOrg, pickOrgId, session } from '@/lib/auth/session';

/**
 * The signed-in app, under top-level paths (/overview, /klev, /settings, ...). The org
 * is not in the URL: it is the user's own org, or for an admin or agency user the org
 * picked in this tab (activeOrg). The backend checks it (X-Org-Id against OrgAcl).
 * Signed-in URLs carry no search params: the date range and every filter are page state
 * (engineer's decision, 2026-10-24). A brand user whose org is still in the signup wizard
 * (metadata.onboarding.step set, not completed) is sent back to that step.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ location }) => {
    const s = await ensureSession();
    if (!s.user) throw redirect({ to: '/sign-in', state: { next: location.href } });
    if (!s.user.email_verified) throw redirect({ to: '/verify-email', state: { email: s.user.email } });
    const orgId = pickOrgId(s.user);
    session.set({ orgId });
    return { user: s.user, orgId };
  },
  loader: async ({ context }) => {
    let org;
    try {
      org = await context.queryClient.query({ ...orgQuery(context.orgId), staleTime: 'static' });
    } catch (e) {
      if (context.orgId === context.user.organization_id) throw e;
      // The org picked in this tab is gone or no longer allowed: fall back to the user's own org.
      activeOrg.write(null);
      throw redirect({ to: '/overview' });
    }
    if (!canSwitchOrg(context.user)) {
      const step = getServerOnboardingRoute(org);
      if (step) throw redirect({ to: step });
    }
    return org;
  },
  component: OrgLayout,
  pendingComponent: () => (
    <div className="flex min-h-screen">
      <div className="w-[248px] bg-surface-static-neutral-accented" />
      <div className="flex flex-1 flex-col gap-6 p-8">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="flex min-h-screen items-center justify-center">
      <ErrorState error={error} onRetry={reset} />
    </div>
  ),
});

function OrgLayout() {
  const { orgId } = Route.useRouteContext();
  return (
    <AppShell orgId={orgId}>
      <Outlet />
    </AppShell>
  );
}
